import { useMemo } from 'react';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import type {
  QueryAutomationParams,
  RpcStatus,
  V1Automation,
} from '@/generated/hooks/grimoire/models';
import { useQueryAutomation } from '@/generated/hooks/grimoire/v1/api';

import {
  resolveAutomation,
  type ResolvedAutomation,
} from '../utils/code-manifest';

interface IUseResolvedAutomationArgs {
  organizationId: string;
  workspaceId: string;
  automationId: string;
  params: QueryAutomationParams;
  query?: Partial<
    UseQueryOptions<
      AxiosResponse<V1Automation>,
      AxiosError<RpcStatus>,
      AxiosResponse<V1Automation>
    >
  >;
  axios?: AxiosRequestConfig;
}

export function useResolvedAutomation({
  organizationId,
  workspaceId,
  automationId,
  params,
  query,
  axios,
}: IUseResolvedAutomationArgs) {
  const result = useQueryAutomation(
    organizationId,
    workspaceId,
    automationId,
    params,
    { query, axios },
  );

  const automation = useMemo<ResolvedAutomation | undefined>(() => {
    const raw = result.data?.data;
    if (!raw) return undefined;
    return resolveAutomation(raw);
  }, [result.data?.data]);

  return { ...result, automation };
}
